import { isTopic } from '@tunein/web-common';
import { isNil, pickBy } from 'es-toolkit';
import { forEach } from 'es-toolkit/compat';
import { VERBOSE_EVENT_REPORTING_ENABLED } from '../../constants';
import { appSessionDataProvider } from '../../providers';
import { api, player } from '../../singletons';
import { ListenTimeReporter } from './ListenTimeReporter';
import { TunePathMetricsReporter } from './TunePathMetricsReporter';
const errorTypes = {
    sourceFailed: 'sourceFailed',
    allSourcesFailed: 'allSourcesFailed',
    nonFatal: 'nonFatal',
    unknown: 'unknown',
};
export class PlayerReporter {
    #sessionData;
    #topicGuideId;
    #events;
    #listenTimeReporter;
    #tunePathMetricsReporter;
    #hadOnlyHttpStreams = false;
    constructor() {
        this.#listenTimeReporter = new ListenTimeReporter();
        this.#tunePathMetricsReporter = new TunePathMetricsReporter();
        this.#events = {
            loadstart: this.#onLoadStart,
            stalled: this.#onStalled,
            emptied: this.#onEmptied,
            stopped: this.#onStopped,
            pause: this.#onPause,
            playing: this.#onPlaying,
            ended: this.#onEnded,
            waiting: this.#onWaiting,
            ratechange: this.#onRateChange,
            error: this.#onError,
            retrying: this.#onRetrying,
        };
    }
    #registerEvents() {
        // Prevents duplicate event listeners
        this.#deregisterEvents();
        forEach(this.#events, (cb, event) => {
            player.on(event, cb);
        });
    }
    #deregisterEvents() {
        forEach(this.#events, (cb, event) => {
            player.off(event, cb);
        });
    }
    #reportEvent(eventName) {
        if (api.getConfigSetting(VERBOSE_EVENT_REPORTING_ENABLED)) {
            api.telemetry.reportSandbox({
                category: 'player',
                action: 'event',
                props: { eventName },
            });
        }
    }
    #onLoadStart = () => {
        this.#reportEvent('loadstart');
    };
    #onStalled = () => {
        this.#reportEvent('stalled');
    };
    #onEmptied = () => {
        this.#reportEvent('emptied');
    };
    #onSourceFailed = (errorEvent) => {
        this.#logPlayerError(errorTypes.sourceFailed, errorEvent);
    };
    #onAllSourcesFailed = (event) => {
        const { index, error, source = {} } = event;
        const { id: streamGuideId, isHlsFormatIncorrect = false, removedM3UExtension = false, isShoutcastUrl = false, format = '', } = source;
        this.#listenTimeReporter.reportFailure(streamGuideId, {
            index,
            isShoutcastHack: isShoutcastUrl,
            isFinalStream: true,
            isHlsFormatIncorrect,
            removedM3UExtension,
            format,
        }, error);
        this.#logPlayerError(errorTypes.allSourcesFailed, {
            error,
        });
        this.#tunePathMetricsReporter.reportFailure();
        this.stopSession();
    };
    #onPlaying = (event) => {
        const { index, isLastSource, source = {} } = event;
        const { id: streamGuideId, isShoutcastUrl = false, isHlsFormatIncorrect = false, removedM3UExtension = false, format = '', } = source;
        const playbackRate = player.getPlaybackRate() ?? 1;
        this.#tunePathMetricsReporter.onPlaying();
        this.#listenTimeReporter.onPlaying(streamGuideId, {
            index,
            isShoutcastHack: isShoutcastUrl,
            isFinalStream: isLastSource,
            isHlsFormatIncorrect,
            removedM3UExtension,
            format,
        }, playbackRate);
    };
    #onStopped = () => {
        this.stopSession(true);
    };
    #onPause = () => {
        const duration = player.getDuration();
        const currentTime = player.getCurrentTime();
        // When an on-demand stream reaches its end, the browser will emit a paused event. We only want to report on a
        // user-initiated pause. The current time can exceed the duration by a few milliseconds, so we need to check if the
        // current time is greater than or equal to the duration.
        if (currentTime >= duration && !player.isLiveSeekStream) {
            return;
        }
        this.#listenTimeReporter.reportPause();
        this.#tunePathMetricsReporter.pauseTimer();
    };
    #onEnded = () => {
        this.#listenTimeReporter.reportEnd();
        this.stopSession();
        this.#reportEvent('ended');
    };
    #onWaiting = () => {
        this.#listenTimeReporter.setBufferStartTime();
        this.#reportEvent('waiting');
    };
    #onRateChange = () => {
        const playbackRate = player.getPlaybackRate() ?? 1;
        this.#listenTimeReporter.setPlaybackRate(playbackRate);
    };
    #onError = (event) => {
        if (!event.error.fatal) {
            this.#logPlayerError(errorTypes.nonFatal, event);
            return;
        }
        if (event.isLastSource) {
            this.#onAllSourcesFailed(event);
            return;
        }
        this.#onSourceFailed(event);
    };
    #onRetrying = (event) => {
        api.telemetry.reportSandbox({
            category: 'player',
            action: 'retrying',
            props: { attempt: event?.attempt?.toString() || '' },
        });
    };
    #logPlayerError(type, event) {
        const { guideId } = this.#sessionData.guideItem;
        const { error, source } = event;
        const { id: streamGuideId, isShoutcastUrl, format } = (source || {});
        const context = pickBy({
            error: error instanceof Error ? error.message : error,
            type,
            guideId,
            streamGuideId,
            format,
            isShoutcastUrl,
        }, (v) => !isNil(v));
        api.logError(error?.message || 'Unknown Error', context, false);
    }
    stopSession(reportStop = false) {
        this.#deregisterEvents();
        this.#tunePathMetricsReporter.clearTimer();
        if (reportStop) {
            this.#listenTimeReporter.reportStop();
        }
        this.#hadOnlyHttpStreams = false;
    }
    startSession(sessionData) {
        const { guideItem, listenId, isUserInitiated } = sessionData;
        const { guideId } = guideItem;
        this.stopSession(isUserInitiated);
        this.#sessionData = sessionData;
        this.#topicGuideId = isTopic(guideId) ? guideId : '';
        this.#tunePathMetricsReporter.startSession(this.#sessionData);
        this.#listenTimeReporter.startSession(this.#sessionData);
        api.telemetry.reportSandbox({
            category: 'player',
            action: 'load',
            props: {
                autoplay: player.getOptions()?.autoPlay?.toString() || 'false',
                isUserInitiated: isUserInitiated?.toString() || 'false',
            },
        });
        if (isUserInitiated) {
            api.telemetry.reportUserPlayClicked({
                guideId,
                listenId: listenId.toString(),
                parentGuideId: this.#topicGuideId ? guideId : '',
                breadcrumbId: appSessionDataProvider.breadcrumbId,
            });
        }
    }
    onStartStreamLoad(streams, hadhadOnlyHttpStreams) {
        const { guideItem, listenId } = this.#sessionData;
        this.#hadOnlyHttpStreams = hadhadOnlyHttpStreams || false;
        this.#registerEvents();
        this.#tunePathMetricsReporter.onStartStreamLoad();
        this.#listenTimeReporter.onStartStreamLoad({
            position: streams[0]?.position,
            onlyHttpStreams: this.#hadOnlyHttpStreams,
        });
        api.telemetry.reportListenSessionStarted({
            listenId: listenId.toString(),
            guideId: guideItem.guideId,
            parentGuideId: guideItem.parentGuideId || '',
            // TODO: we should deprecate this on the event layer and set it on the context instead (from the app). There might
            //  be some push back, so the next best option will be to drill down this one piece of data from the app layer to here.
            breadcrumbId: appSessionDataProvider.breadcrumbId,
            isStreamPrebuffered: false,
            prebufferTimeMs: 0,
        });
    }
    reportSeek({ seekTime = 0, seekOffset = 0 }) {
        const currentTime = player.getCurrentTime();
        const duration = player.getDuration();
        const newTime = seekOffset ? currentTime + seekOffset : seekTime;
        this.#listenTimeReporter.reportShift(newTime, currentTime, duration);
    }
    reportUnknownLoadError(error) {
        this.#logPlayerError(errorTypes.unknown, { error });
    }
}
